import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { AuthenticatedStudent, AuthState, LoginResponse } from '../models';

interface StoredAuthSession {
  readonly user: AuthenticatedStudent;
  readonly accessToken?: string;
  readonly expiresAt?: string;
}

const AUTH_SESSION_KEY = 'studentPortal.auth.session';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly platformId = inject(PLATFORM_ID);

  getSession(): StoredAuthSession | null {
    if (!this.canUseStorage()) {
      return null;
    }

    return this.readSessionFromStorage(localStorage) ?? this.readSessionFromStorage(sessionStorage);
  }

  saveSession(response: LoginResponse, rememberMe: boolean): AuthState {
    const session: StoredAuthSession = {
      user: response.user,
      accessToken: response.accessToken,
      expiresAt: response.expiresAt,
    };

    // TODO: Prefer backend HttpOnly Cookie session when the production auth API is ready.
    if (this.canUseStorage()) {
      const targetStorage = rememberMe ? localStorage : sessionStorage;
      const otherStorage = rememberMe ? sessionStorage : localStorage;

      targetStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
      otherStorage.removeItem(AUTH_SESSION_KEY);
    }

    return {
      user: response.user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      accessToken: response.accessToken,
      expiresAt: response.expiresAt,
    };
  }

  clearSession(): void {
    if (!this.canUseStorage()) {
      return;
    }

    localStorage.removeItem(AUTH_SESSION_KEY);
    sessionStorage.removeItem(AUTH_SESSION_KEY);
  }

  private readSessionFromStorage(storage: Storage): StoredAuthSession | null {
    const rawSession = storage.getItem(AUTH_SESSION_KEY);

    if (!rawSession) {
      return null;
    }

    try {
      return JSON.parse(rawSession) as StoredAuthSession;
    } catch {
      storage.removeItem(AUTH_SESSION_KEY);
      return null;
    }
  }

  private canUseStorage(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
